/**
 * AIRA Voice Client - Phase 3.2 + 3.4 Enhancements
 * 
 * WebSocket voice client that connects to /api/aira-voice/ws
 * Handles:
 * - Browser microphone capture
 * - Audio streaming to backend
 * - Voice protocol events
 * - State management
 * - Audio playback
 * - Phase 3.4: Improved reconnection
 * - Phase 3.4: Enhanced barge-in handling
 * 
 * Uses real Deepgram STT and OpenAI TTS providers
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// Voice session states (matching backend VoiceSessionState)
export const VOICE_STATES = {
  IDLE: 'idle',
  INITIALIZING: 'initializing',
  CONNECTED: 'connected',
  LISTENING: 'listening',
  TRANSCRIBING: 'transcribing',
  UNDERSTANDING: 'understanding',
  THINKING: 'thinking',
  SEARCHING: 'searching',
  PLANNING: 'planning',
  EXECUTING: 'executing',
  SPEAKING: 'speaking',
  SUCCESS: 'success',
  INTERRUPTED: 'interrupted',
  ERROR: 'error',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting', // Phase 3.4
};

// Voice event types (matching backend VoiceEventType)
export const VOICE_EVENTS = {
  // Client → Server
  SESSION_START: 'session_start',
  AUDIO_START: 'audio_start',
  AUDIO_CHUNK: 'audio_chunk',
  AUDIO_END: 'audio_end',
  USER_INTERRUPT: 'user_interrupt',
  SESSION_END: 'session_end',
  PING: 'ping',
  
  // Server → Client
  SESSION_READY: 'session_ready',
  LISTENING: 'listening',
  UNDERSTANDING: 'understanding',
  THINKING: 'thinking',
  TRANSCRIPT_PARTIAL: 'transcript_partial',
  TRANSCRIPT_FINAL: 'transcript_final',
  SPEAKING: 'speaking',
  AUDIO_CHUNK_SERVER: 'audio_chunk',
  TURN_COMPLETE: 'turn_complete',
  INTERRUPTION_ACK: 'interruption_ack',
  ERROR: 'error',
  PONG: 'pong',
};

// Audio configuration
const AUDIO_CONFIG = {
  sampleRate: 16000,
  channelCount: 1,
  bitsPerSample: 16,
  chunkSize: 4096,
  mimeType: 'audio/webm;codecs=opus',
};

// Phase 3.4: Reconnection configuration
const RECONNECT_CONFIG = {
  maxAttempts: 5,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2.0,
};

/**
 * useVoiceClient Hook
 * 
 * Manages WebSocket voice connection and audio streaming
 */
export function useVoiceClient(options = {}) {
  const {
    autoConnect = false,
    language = 'en',
    voiceId = 'nova',
    onStateChange,
    onTranscript,
    onError,
    onAudioReceived,
    onSessionReady,
  } = options;

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [voiceState, setVoiceState] = useState(VOICE_STATES.IDLE);
  
  // Transcript state
  const [partialTranscript, setPartialTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  
  // Audio state
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micPermission, setMicPermission] = useState(null);
  
  // Provider info
  const [providerInfo, setProviderInfo] = useState({ stt: 'unknown', tts: 'unknown' });
  
  // Refs
  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const audioQueueRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Phase 3.4: Reconnection state
  const reconnectAttemptRef = useRef(0);
  const reconnectDelayRef = useRef(RECONNECT_CONFIG.initialDelayMs);
  const intentionalDisconnectRef = useRef(false);

  // API URL
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  /**
   * Update voice state and notify callback
   */
  const updateVoiceState = useCallback((newState) => {
    setVoiceState(newState);
    onStateChange?.(newState);
  }, [onStateChange]);

  /**
   * Send event to WebSocket
   */
  const sendEvent = useCallback((eventType, data = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: eventType,
        ...data,
      }));
    }
  }, []);

  /**
   * Handle incoming WebSocket message
   */
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      const eventType = data.type;

      switch (eventType) {
        case VOICE_EVENTS.SESSION_READY:
          setSessionId(data.session_id);
          setIsConnected(true);
          updateVoiceState(VOICE_STATES.CONNECTED);
          // Phase 3.4: Reset reconnection state on successful connect
          reconnectAttemptRef.current = 0;
          reconnectDelayRef.current = RECONNECT_CONFIG.initialDelayMs;
          onSessionReady?.(data);
          // Send session_start
          sendEvent(VOICE_EVENTS.SESSION_START, {
            language,
            voice_id: voiceId,
            subtitles_enabled: true,
          });
          break;

        case VOICE_EVENTS.LISTENING:
          updateVoiceState(VOICE_STATES.LISTENING);
          break;

        case VOICE_EVENTS.UNDERSTANDING:
          updateVoiceState(VOICE_STATES.UNDERSTANDING);
          break;

        case VOICE_EVENTS.THINKING:
          updateVoiceState(VOICE_STATES.THINKING);
          break;

        case VOICE_EVENTS.TRANSCRIPT_PARTIAL:
          setPartialTranscript(data.transcript || '');
          onTranscript?.(data.transcript, false);
          break;

        case VOICE_EVENTS.TRANSCRIPT_FINAL:
          setFinalTranscript(data.transcript || '');
          setPartialTranscript('');
          onTranscript?.(data.transcript, true);
          break;

        case VOICE_EVENTS.SPEAKING:
          updateVoiceState(VOICE_STATES.SPEAKING);
          setIsSpeaking(true);
          break;

        case VOICE_EVENTS.AUDIO_CHUNK_SERVER:
          if (data.audio_data) {
            audioQueueRef.current.push(data.audio_data);
            playAudioQueue();
            onAudioReceived?.(data.audio_data, data.sequence);
          }
          break;

        case VOICE_EVENTS.TURN_COMPLETE:
          updateVoiceState(VOICE_STATES.SUCCESS);
          setIsSpeaking(false);
          // Return to connected state
          setTimeout(() => {
            if (voiceState !== VOICE_STATES.DISCONNECTED) {
              updateVoiceState(VOICE_STATES.CONNECTED);
            }
          }, 1000);
          break;

        case VOICE_EVENTS.INTERRUPTION_ACK:
          updateVoiceState(VOICE_STATES.INTERRUPTED);
          setIsSpeaking(false);
          stopAudioPlayback();
          break;

        case VOICE_EVENTS.ERROR:
          console.error('Voice error:', data.error_message);
          updateVoiceState(VOICE_STATES.ERROR);
          onError?.(data.error_code, data.error_message);
          if (!data.recoverable) {
            disconnect();
          }
          break;

        case VOICE_EVENTS.PONG:
          // Keepalive response
          break;

        default:
          console.log('Unknown voice event:', eventType, data);
      }
    } catch (err) {
      console.error('Failed to parse voice message:', err);
    }
  }, [language, voiceId, onStateChange, onTranscript, onError, onAudioReceived, updateVoiceState, sendEvent]);

  /**
   * Play audio from queue
   */
  const playAudioQueue = useCallback(async () => {
    if (audioQueueRef.current.length === 0) return;
    if (audioPlayerRef.current) return; // Already playing

    const base64Audio = audioQueueRef.current.shift();
    if (!base64Audio) return;

    try {
      // Decode base64 to audio
      const audioBytes = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioBytes.length);
      const view = new Uint8Array(arrayBuffer);
      for (let i = 0; i < audioBytes.length; i++) {
        view[i] = audioBytes.charCodeAt(i);
      }

      // Create audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Decode and play
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      
      audioPlayerRef.current = source;
      
      source.onended = () => {
        audioPlayerRef.current = null;
        playAudioQueue(); // Play next in queue
      };
      
      source.start(0);
    } catch (err) {
      console.error('Audio playback error:', err);
      audioPlayerRef.current = null;
      playAudioQueue(); // Try next
    }
  }, []);

  /**
   * Stop audio playback
   */
  const stopAudioPlayback = useCallback(() => {
    if (audioPlayerRef.current) {
      try {
        audioPlayerRef.current.stop();
      } catch (e) {
        // Ignore
      }
      audioPlayerRef.current = null;
    }
    audioQueueRef.current = [];
    setIsSpeaking(false);
  }, []);

  /**
   * Connect to voice WebSocket
   */
  const connect = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    updateVoiceState(VOICE_STATES.INITIALIZING);

    // Get auth token from localStorage or cookies
    const token = localStorage.getItem('auth_token') || '';

    const wsUrl = `${WS_URL}/api/aira-voice/ws?language=${language}&voice_id=${voiceId}${token ? `&token=${token}` : ''}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Voice WebSocket connected');
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          sendEvent(VOICE_EVENTS.PING);
        }, 30000);
      };

      wsRef.current.onmessage = handleMessage;

      wsRef.current.onerror = (error) => {
        console.error('Voice WebSocket error:', error);
        updateVoiceState(VOICE_STATES.ERROR);
        onError?.('WS_ERROR', 'WebSocket connection error');
      };

      wsRef.current.onclose = (event) => {
        console.log('Voice WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        setSessionId(null);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Phase 3.4: Enhanced reconnection logic
        if (!intentionalDisconnectRef.current && event.code !== 1000) {
          // Unintentional disconnect - attempt reconnection
          if (reconnectAttemptRef.current < RECONNECT_CONFIG.maxAttempts) {
            updateVoiceState(VOICE_STATES.RECONNECTING);
            reconnectAttemptRef.current += 1;
            
            console.log(`Reconnecting (attempt ${reconnectAttemptRef.current}/${RECONNECT_CONFIG.maxAttempts}) in ${reconnectDelayRef.current}ms...`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
              // Increase delay for next attempt with exponential backoff
              reconnectDelayRef.current = Math.min(
                reconnectDelayRef.current * RECONNECT_CONFIG.backoffMultiplier,
                RECONNECT_CONFIG.maxDelayMs
              );
            }, reconnectDelayRef.current);
          } else {
            // Max attempts reached
            updateVoiceState(VOICE_STATES.DISCONNECTED);
            onError?.('RECONNECT_FAILED', 'Maximum reconnection attempts reached');
          }
        } else {
          updateVoiceState(VOICE_STATES.DISCONNECTED);
        }
      };
    } catch (err) {
      console.error('Failed to connect:', err);
      updateVoiceState(VOICE_STATES.ERROR);
      onError?.('CONNECT_ERROR', err.message);
    }
  }, [WS_URL, language, voiceId, handleMessage, updateVoiceState, sendEvent, onError]);

  /**
   * Disconnect from voice WebSocket
   */
  const disconnect = useCallback(() => {
    // Phase 3.4: Mark as intentional disconnect
    intentionalDisconnectRef.current = true;
    
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Stop recording
    stopRecording();

    // Stop audio
    stopAudioPlayback();

    // Send session_end
    sendEvent(VOICE_EVENTS.SESSION_END);

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setSessionId(null);
    updateVoiceState(VOICE_STATES.IDLE);
    
    // Reset intentional flag for next connection
    intentionalDisconnectRef.current = false;
    // Reset reconnection state
    reconnectAttemptRef.current = 0;
    reconnectDelayRef.current = RECONNECT_CONFIG.initialDelayMs;
  }, [sendEvent, stopAudioPlayback, updateVoiceState]);

  /**
   * Request microphone permission
   */
  const requestMicPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop()); // Release immediately
      setMicPermission('granted');
      return true;
    } catch (err) {
      console.error('Microphone permission denied:', err);
      setMicPermission('denied');
      return false;
    }
  }, []);

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    if (!isConnected) {
      console.warn('Not connected to voice service');
      return false;
    }

    if (isRecording) {
      return true;
    }

    try {
      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: AUDIO_CONFIG.sampleRate,
          channelCount: AUDIO_CONFIG.channelCount,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      mediaStreamRef.current = stream;
      setMicPermission('granted');

      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: AUDIO_CONFIG.sampleRate,
      });

      const source = audioContextRef.current.createMediaStreamSource(stream);
      
      // Create script processor for audio chunks
      processorRef.current = audioContextRef.current.createScriptProcessor(
        AUDIO_CONFIG.chunkSize,
        AUDIO_CONFIG.channelCount,
        AUDIO_CONFIG.channelCount
      );

      processorRef.current.onaudioprocess = (e) => {
        if (!isRecording) return;

        const inputData = e.inputBuffer.getChannelData(0);
        
        // Convert float32 to int16
        const int16Data = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        // Convert to base64
        const bytes = new Uint8Array(int16Data.buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);

        // Send audio chunk
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: VOICE_EVENTS.AUDIO_CHUNK,
            audio_data: base64,
          }));
        }
      };

      source.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      // Send audio_start event
      sendEvent(VOICE_EVENTS.AUDIO_START);
      setIsRecording(true);
      
      return true;
    } catch (err) {
      console.error('Failed to start recording:', err);
      setMicPermission('denied');
      onError?.('MIC_ERROR', err.message);
      return false;
    }
  }, [isConnected, isRecording, sendEvent, onError]);

  /**
   * Stop recording audio
   */
  const stopRecording = useCallback(() => {
    if (!isRecording) return;

    // Stop processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Send audio_end event
    sendEvent(VOICE_EVENTS.AUDIO_END);
    setIsRecording(false);
  }, [isRecording, sendEvent]);

  /**
   * Interrupt AIRA while speaking (Phase 3.4 enhanced barge-in)
   */
  const interrupt = useCallback((reason = 'user_action') => {
    // Stop audio playback immediately for responsive barge-in
    stopAudioPlayback();
    // Clear any pending audio in queue
    audioQueueRef.current = [];
    // Send interrupt event to backend
    sendEvent(VOICE_EVENTS.USER_INTERRUPT, { reason });
    // Update local state to reflect interruption
    updateVoiceState(VOICE_STATES.INTERRUPTED);
  }, [sendEvent, stopAudioPlayback, updateVoiceState]);

  /**
   * Fetch voice service status
   */
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/aira-voice/status`);
      const data = await response.json();
      setProviderInfo({
        stt: data.stt?.provider || 'unknown',
        tts: data.tts?.provider || 'unknown',
      });
      return data;
    } catch (err) {
      console.error('Failed to fetch voice status:', err);
      return null;
    }
  }, [API_URL]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return hook interface
  return {
    // Connection
    isConnected,
    sessionId,
    connect,
    disconnect,
    
    // State
    voiceState,
    VOICE_STATES,
    
    // Recording
    isRecording,
    startRecording,
    stopRecording,
    micPermission,
    requestMicPermission,
    
    // Audio
    isSpeaking,
    interrupt,
    
    // Transcripts
    partialTranscript,
    finalTranscript,
    
    // Provider info
    providerInfo,
    fetchStatus,
    
    // Utilities
    sendEvent,
  };
}

export default useVoiceClient;
